import { currentUser } from "@/App";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import CopyLink from "@/components/CopyLink";
import { useInvites } from "@/services/hooks";

function InviteCard({ invite }) {
  const url = `${window.location.origin}/${currentUser.key}?invite=${invite.code}`;
  const total = invite.free_limit ?? 0;
  const used = invite.used ?? 0;
  const remaining = Math.max(total - used, 0);
  const progress = total > 0 ? Math.min((used / total) * 100, 100) : 0;

  const isFull = total > 0 && remaining <= 0;

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: "16px",
        backgroundColor: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Chip
          label={invite.code}
          size="small"
          variant="outlined"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "text.secondary",
            borderColor: "rgba(255,255,255,0.15)",
          }}
        />
        <Typography
          variant="h6"
          sx={{ color: "secondary.main", lineHeight: 1 }}
        >
          {isFull ? "Full" : `${remaining} left`}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", mt: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 5,
              backgroundColor: (theme) =>
                theme.palette[isFull ? "secondary" : "primary"].main,
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 0.75,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {used} / {total} used
          </Typography>
          {invite.free_days ? (
            <Typography variant="caption" color="text.secondary">
              {invite.free_days}-day access
            </Typography>
          ) : null}
        </Box>
      </Box>

      {isFull ? (
        <Box
          sx={{
            mt: 3,
            py: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <CheckCircleRounded sx={{ fontSize: 48, color: "secondary.main" }} />
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
            All slots claimed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {invite.free_days
              ? `Everyone who joined keeps their ${invite.free_days}-day access.`
              : "Everyone who joined keeps their access."}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              p: 2,
              borderRadius: "12px",
              mt: 3,
              display: "inline-flex",
            }}
          >
            <QRCodeSVG value={url} size={160} />
          </Box>

          <Box sx={{ mt: 2, width: "100%" }}>
            <CopyLink url={url} />
          </Box>
        </>
      )}
    </Box>
  );
}

export default function Promo() {
  const { data: invites, isLoading } = useInvites(currentUser.key);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6">Invites</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Share this link to invite fans to access without a credit card.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !invites?.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          No active invites. Contact the Notebook team to get started.
        </Typography>
      ) : (
        <Stack>
          {invites.map((invite) => (
            <InviteCard key={invite.code} invite={invite} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
